<template>
    <IntegrationModal
        :title="$t('vue.slack')"
        documentation-url=""
        :invalid-validation="v$.$invalid"
        :loading="loading"
        :edit-mode="integration !== undefined"
        @close="$emit('close')"
        @create="createOAuthUrl"
        @edit="updateIntegration"
    >
        <template #inputs>
            <TextInput
                v-model="v$.name.$model"
                class="mt-6"
                :placeholder="$t('vue.enter_your_preferred_integration_name')"
                :label="$t('vue.name')"
                :error="getError('name')"
            />
        </template>
    </IntegrationModal>
</template>

<script lang="ts" setup>
import route from "ziggy-js";
import { create } from "@composables/useNotifications";
import useVuelidate from "@vuelidate/core";
import { maxLength } from "@vuelidate/validators";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import type { PropType } from "vue";
import type { IIntegration, ISlack } from "@/types/Integrations/IIntegration";

const props = defineProps({
    integration: {
        type: Object as PropType<IIntegration<ISlack>>,
        required: false,
        default: undefined,
    },
});
const emit = defineEmits(["close"]);

const loading = ref(false);
const errors = ref<IInputErrors>({});

const state = reactive({
    name: props.integration?.name || "",
});

const rules = {
    name: {
        maxLength: maxLength(255),
    },
};

const v$ = useVuelidate(rules, state);

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || errors.value?.[field] || "";
};

const createOAuthUrl = async () => {
    v$.value.$touch();
    if (v$.value.$error) {
        () => undefined;
    } else {
        loading.value = true;
        try {
            const result = await axios.get(
                route("integrations.slack.o-auth-two-url", {
                    name: state.name,
                }),
            );

            if (result.data.success) {
                emit("close");
                window.location.href = result.data.data;
            } else {
                create({
                    title: $t("vue.integration_not_added"),
                    text: $t("vue.error_adding_slack_integration"),
                    type: "error",
                });
            }
        } catch (e: any) {
            create({
                title: $t("vue.integration_not_added"),
                text:
                    e.response?.data?.message ??
                    $t("vue.error_adding_slack_integration"),
                type: "error",
            });
        } finally {
            loading.value = false;
        }
    }
};

const updateIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error || !props.integration) {
        () => undefined;
    } else {
        loading.value = true;
        router.put(
            route("integrations.slack.update", {
                integration: props.integration.id,
            }),
            {
                name: state.name,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    emit("close");
                    create({
                        title: $t("vue.integration_updated"),
                        type: "success",
                    });
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: $t("vue.integration_not_updated"),
                        text:
                            err.message ??
                            $t("vue.error_updating_slack_integration"),
                        type: "error",
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            },
        );
    }
};
</script>

<style scoped></style>
